import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AsyncCreatableSelect from 'react-select/async-creatable';

import { selectStyles } from '../../../constants/select';

import InputError from '../inputError';

// hooks
import { useApiRequest } from '../../../hooks/useApiRequest';

const ClientSelect = ({
  label,
  placeholder,
  hideLabel,
  name,
  value,
  error,
  errorMsg,
  handleChange,
}) => {
  const apiRequest = useApiRequest();

  const getClientOptions = useCallback(
    async inputValue => {
      if (!inputValue || inputValue.length < 2) {
        return [];
      }

      const {
        data: { clients },
      } = await apiRequest.get(`/v1/clients/search?query=${inputValue}`);

      return clients.map(client => ({
        value: client.id,
        label: client.name,
      }));
    },
    [apiRequest]
  );

  const getCreateLabel = inputValue => {
    return `Add new Company: ${inputValue}`;
  };

  const buildEvent = eventValue => ({
    currentTarget: {
      name,
      value: eventValue,
      type: 'clientSelect',
    },
  });

  const handleChangeOption = newValue => {
    const event = buildEvent(newValue);
    handleChange(event);
  };

  const handleCreateOption = newLabel => {
    const event = buildEvent({
      value: 0,
      label: newLabel,
    });
    handleChange(event);
  };

  const classes = classNames({
    field: true,
    'field--client-select': true,
    'field--hidden-label': hideLabel,
    'field--has-error': !!error,
  });

  const dropdownIndicator = () => ({ display: 'none' });
  const clientSelectStyles = { ...selectStyles, dropdownIndicator };

  return (
    <div className={classes} data-testid="clientSelect">
      <label className="field__label" htmlFor={name}>
        {label}
      </label>
      <AsyncCreatableSelect
        placeholder={placeholder}
        name={name}
        styles={clientSelectStyles}
        cacheOptions
        defaultOptions
        isClearable
        loadOptions={getClientOptions}
        formatCreateLabel={getCreateLabel}
        classNamePrefix={name}
        value={value}
        onChange={handleChangeOption}
        onCreateOption={handleCreateOption}
      />
      {error && <InputError errorMsg={errorMsg} />}
    </div>
  );
};

ClientSelect.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  hideLabel: PropTypes.bool,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  error: PropTypes.bool,
  errorMsg: PropTypes.string,
  handleChange: PropTypes.func,
};

ClientSelect.defaultProps = {
  placeholder: null,
  hideLabel: true,
  value: '',
  error: false,
  errorMsg: '',
  handleChange: () => null,
};

export default ClientSelect;
